// @flow

import _ from 'lodash';
import numeral from 'numeral';

import DataTable from '../core/charts/Datatable';
import dialog from '../core/dialog';
import infoBoard from '../core/infoboard';
import worldMap from '../core/map';
import dataBoard from '../core/databoard';
import templates from '../core/templates';
import translate, { translateText } from '../core/tools/translate';
import environment from '../environment';
import { customScrollbar } from '../shared';
import { notify, NOTIFY_TYPE } from '../shared/notify';
import api from '../api';
import DistanceReportInfo from './DistanceReportInfo';
import type { originDistanceInfo, routingOption } from './DistanceReportInfo';
import Workspace from '../workspaces';
import { ControlType, Item } from '../workspaces/item';
import { Control } from 'leaflet';
import { FeaturePermission } from '../../constants/feature.permission.helper';
import { LocaleTypeEnum } from '../../enuns/localeTypeNeocom.enum.js';
import { StoreType } from '../../enuns/store.enun';
import { SaleTypeEnum } from '../../enuns/saleType.enum';
const LayerInfo = (name) => require('../info').get(name);
const LayerControls = (name) => require('../controls').get(name);
const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

const localeType = {
    city: 3
};

class ShapesInfo {
    constructor (options) {
        this.check = this.check.bind(this);
        this.options = _.assign({}, this.options, options);

        this._status = false;
        this._mainContainer = null;
        this._selectedAreaId = undefined;
    }

    init () {
    }

    getLayer () {
        this.options.layer = LayerControls(this.options.name).getLayer();
        return this.options.layer;
    }

    _getTemplate (type, values) {
        if (_.isArray(type)) {
            return templates(`${type[0]}-${type[1]}`, values);
        } else {
            return templates(`shared-${type}`, values);
        }
    }

    _getContainer () {
        return infoBoard.createContainer({
            id: this.options.name,
            name: this.options.label,
            actions: this._hasSummary()
        });
    }

    setCacheEntries (item, add = true){
        let cacheEntry = [];
        const cache = environment.get('DNC.ENTRIES');
        if(!_.isUndefined(cache)) _.map(cache, f => {
            cacheEntry.push(f);
        });

        if (add) cacheEntry.push(item);
        else _.remove(cacheEntry, g => _.isEqual(g, item));

        environment.set('DNC.ENTRIES', cacheEntry);
    }

    _content (container, feature, index, autoHighlight = true) {
        const options = {
            check: this.check,
            container,
            feature,
            index,
            name: this.options.name,
            originalName: this.options.name,
            controls: {
                actions: this._hasActions(),
                //summary: this._hasSummary(),
                selection: this._hasSummary()
            }
        };

        if (feature.properties.disabled) {
            return;
        }

        const item = infoBoard.createItem(options);
        const layer = this.getEntry().get(String(feature.properties.Id)) || this.getLayer().getLayer(feature.properties.Id);

        if (!item) return;

        if (item.btn.summary) {
            item.btn.summary.on('click', (e) => {
                this._showSummary(feature);
                Workspace.addAction(new Item(`#${item.btn.summary.prop('id')}`, '', ControlType.InputButton));
            });
        }

        if (item.btn.selection) {
            item.btn.selection.on('ifChecked', (e) => {
                this.add(item.feature);
                this.setCacheEntries(item.feature, true);
                Workspace.addAction(new Item(`#${e.target.id}`, true, ControlType.ICheckBox));
            });
            item.btn.selection.on('ifUnchecked', (e) => {
                this.setCacheEntries(item.feature, false);
                this.remove(item.feature);
                Workspace.addAction(new Item(`#${e.target.id}`, false, ControlType.ICheckBox));
            });
        }

        if (autoHighlight) {
            if (this.check(feature.properties.Id)) {
                this.highlight(feature, {
                    layer,
                    fixed: true,
                    force: true
                });
            }
        }

        return item;
    }

    _resetCustomersSummaryFilters() {
        this._customersSummaryFilters = {
            groups: [],
            groupClient: [],
            dealers: [],
            fuelTypes: [],
            segments: [],
            saleTypes: []
        };
        this._customersSummaryStores = null;
    }

    _setCustomersSummaryStores(customers) {
        const idsStore = _(customers).map('idStore').uniq().value();

        this._customersSummaryStores = _.filter(
            environment.get('CONFIG.FILTERS.DEALERS_STORES'),
            s => idsStore.includes(s.Id) && String(s.Tipo).toUpperCase() !== StoreType.TwoS.toUpperCase()
        );
    }

    _getCustomersSummaryStoresByGroups() {
        const { groups, groupClient } = this._customersSummaryFilters;

        return _(this._customersSummaryStores)
            .filter(s => !_.size(groups) || groups.includes(s.Grupo))
            .filter(s => !_.size(groupClient) || groupClient.includes(s.GrupoCliente))
            .value();
    }

    _getCustomersSummaryDealersDataSource() {
        return _(this._getCustomersSummaryStoresByGroups())
            .map(s => ({ text: s.Nome, value: s.Id }))
            .sortBy('text')
            .value();
    }

    _updateCustomersSummaryDealers() {
        const widget = $('#databoard-container [name="databoard-filter-multiselect"][data-id="2"]').data('kendoMultiSelect');
        const dataSource = this._getCustomersSummaryDealersDataSource();
        const available = _.map(dataSource, 'value');

        this._customersSummaryFilters.dealers = _.filter(this._customersSummaryFilters.dealers, id => available.includes(id));

        if (widget) {
            widget.setDataSource(dataSource);
            widget.value(this._customersSummaryFilters.dealers);
        }
    }

    _getCustomersSummaryStoreIds() {
        const { groups, groupClient, dealers } = this._customersSummaryFilters;

        if (!_.size(groups) && !_.size(groupClient) && !_.size(dealers)) {
            return null;
        }

        return _(this._getCustomersSummaryStoresByGroups())
            .filter(s => !_.size(dealers) || dealers.includes(s.Id))
            .map(s => s.Id)
            .value();
    }

    _getCustomersSummaryFilters() {
        const stores = this._customersSummaryStores;
        const toDataSource = (field) => _(stores)
            .map(field)
            .filter(v => !_.isNil(v) && v !== '')
            .uniq()
            .sortBy()
            .map(v => ({ text: v, value: v }))
            .value();

        const multiSelect = (id, key, label, values, visible, onChange) => ({
            id,
            label,
            select: false,
            multiSelect: true,
            value: this._customersSummaryFilters[key],
            values,
            placeholder: `${translate('Common.All')}...`,
            visible,
            change: (e) => {
                this._customersSummaryFilters[key] = e.sender.value();

                if (onChange) {
                    onChange();
                }
            }
        });

        const updateDealers = () => this._updateCustomersSummaryDealers();

        return {
            scrollable: true,
            inputs: [
                multiSelect(0, 'groups', translate('Common.Groups'), toDataSource('Grupo'),
                    isAuthorized(FeaturePermission.LAYER_CUSTOMER.filterGroup), updateDealers),
                multiSelect(1, 'groupClient', `${translate('Common.Group')} ${translate('Common.Client')}`, toDataSource('GrupoCliente'),
                    isAuthorized(FeaturePermission.LAYER_CUSTOMER.showGroupClient) && isAuthorized(FeaturePermission.INFO_SUMMARY.showGroupClient), updateDealers),
                multiSelect(2, 'dealers', translate('Common.Dealer'), this._getCustomersSummaryDealersDataSource(),
                    isAuthorized(FeaturePermission.LAYER_CUSTOMER.filterDealer)),
                multiSelect(3, 'fuelTypes', translate('Common.FuelType'), _.map(environment.get('CONFIG.FILTERS.FUEL_TYPES'), f => ({ text: f.text, value: f.value })),
                    isAuthorized(FeaturePermission.LAYER_CUSTOMER.filterFuelType)),
                multiSelect(4, 'segments', translate('Common.Segments'), _.flatMap(environment.get('CONFIG.FILTERS.COMPETING_SEGMENTS'), val => _.map(val, s => ({ text: translateText(s.nome, true), value: s.id }))),
                    isAuthorized(FeaturePermission.LAYER_CUSTOMER.filterSegment)),
                multiSelect(5, 'saleTypes', translate('Common.Type'), _.filter(environment.get('CONFIG.FILTERS.SALES_TYPE'), s => s.value !== SaleTypeEnum.Total),
                    isAuthorized(FeaturePermission.LAYER_CUSTOMER.filterSaleType)),
                {
                    id: 6,
                    submit: true,
                    visible: true,
                    click: (e) => {
                        e.preventDefault();
                        this._showCustomersPanel();
                    }
                }
            ]
        };
    }

    _showCustomersPanel() {
        const idsStore = this._getCustomersSummaryStoreIds();

        if (idsStore && !_.size(idsStore)) {
            return notify(NOTIFY_TYPE.ERROR, translate('Error.NoDataAvailable'));
        }

        const date = LayerControls(this.options.name).getDate();
        const optionsRequest = {
            Ids: _.map(this.getSelectedEntries(), e => e.Id),
            anoMesFim: date.end.format('YYYYMM'),
            anoMesInicio: date.start.format('YYYYMM'),
            idTema: 24,
            idTipo: this.getType(),
            IdsStore: idsStore,
            idsFuelType: this._customersSummaryFilters.fuelTypes,
            idsSegment: this._customersSummaryFilters.segments,
            idsSaleType: this._customersSummaryFilters.saleTypes
        };
        const canSeeGroupClient = isAuthorized(FeaturePermission.INFO_SUMMARY.showGroupClient);

        api.themes.get.areas.customers(optionsRequest)
            .then((res: any) => {
                if (!this._customersSummaryStores) {
                    this._setCustomersSummaryStores(res);
                }

                const dataSource = {
                    perPercentage: _(res)
                        .groupBy('nameStore')
                        .map((val, key) =>  {
                            return {
                                Nome: key,
                                Total: val.length / res.length
                            };
                        })
                        .orderBy('Total', 'desc')
                        .value(),

                    perTotal: _(res)
                        .groupBy('nameStore')
                        .map((val, key) => {
                            return {
                                Nome: key,
                                Total: val.length
                            };
                        })
                        .orderBy('Total', 'desc')
                        .value(),

                    summary: _(res)
                        .groupBy('nameLocale')
                        .map((loc, key) => {
                            return _(loc)
                                .groupBy('nameStore')
                                .map((store, key) => {
                                    return {
                                        nameLocale: loc[0].nameLocale,
                                        idStoreClient: store[0].idStoreClient,
                                        nameStore: key,
                                        nameEconomicGroup: store[0].nameEconomicGroup,
                                        nameClientGroup: store[0].nameClientGroup,
                                        total: store.length,
                                        percentage: store.length / loc.length
                                    };
                                })
                                .value();
                        })
                        .flatten()
                        .orderBy(['nameLocale', 'total'], ['asc', 'desc'])
                        .value()
                };

                const chartPie = new DataTable(dataSource.perPercentage, {
                    row: translate("Common.Name"),
                    column: [translate("Common.Total")]
                });

                const chartColumn = new DataTable(dataSource.perTotal, {
                    row: translate("Common.Name"),
                    column: [translate("Common.Total")]
                });

                const panel = {
                    title: {
                        table: `${translate(`Common.${this.options.name}`)} - Sumário - ${date.start.format('MM/YYYY')} a ${date.end.format('MM/YYYY')}`,
                        chartPie: `TOP 10 - ${translate("Common.Dealer")} (%)`,
                        chartColumn: `TOP 10 - ${translate("Common.Dealer")}`
                    },
                    dash: true,
                    colors: [
                        null,
                        _.map(_.range(0, dataSource.perTotal.length), () => "#3498db")
                    ],
                    data: [dataSource.summary, chartPie, chartColumn],
                    convertTable: false,
                    format: ["{0:n0}", "{0:p2}", "{0:n0}"],
                    columns: [
                        {
                            field: "nameLocale",
                            title: "Area"
                        },
                        {
                            field: "idStoreClient",
                            title: 'Dealer Number'
                        },
                        {
                            field: "nameStore",
                            title: translate("Common.Name")
                        },
                        {
                            field: "nameEconomicGroup",
                            title: translate("Common.Group")
                        },
                        {
                            field: "total",
                            title: translate("Common.Total"),
                            width: 100,
                            template: dataSource => numeral(dataSource.total).format("0,0")
                        },
                        {
                            field: "percentage",
                            title: `${translate("Common.Total")} (%)`,
                            width: 100,
                            template: dataSource =>
                                numeral(dataSource.percentage).format("0,0[.]00%")
                        }
                    ]
                };

                if (canSeeGroupClient) {
                    panel.columns.splice(4, 0, {
                        field: 'nameClientGroup',
                        title: `${translate('Common.Group')} ${translate('Common.Client')}`
                    });
                }

                panel.filters = this._getCustomersSummaryFilters();

                dataBoard.show(panel);
            })
            .catch(console.error);
    }

    _showSummary (feature) {
        const opt = {
            type: this.getType,
            context: this.options.context,
            visible: true,
            grouping: _.some(environment.get('CONFIG.FILTERS.MARKED_AREA_TYPES'), v => v.name === LayerControls(this.options.name).getTypeText())
        };

        if (feature) {
            opt.id = feature.properties.Id;
            opt.title = feature.properties['Nome'];
        } else {
            opt.id = _.map(this.getSelectedEntries(), e => e.Id);
            opt.locations = {
                title: translate('Common.Locations'),
                chart: new DataTable(_.map(this.getSelectedEntries(), e => ({
                    [translate('Common.Name')]: e.Nome,
                    [translate('Common.Complement')]: (e.Complemento || '').replace(new RegExp(`${e.Nome},? ?`), '').toASCII(),
                    [translate('Common.Type')]: e.Tipo
                })), {
                    column: [translate('Common.Complement'), translate('Common.Type')],
                    row: translate('Common.Name')
                })
            };
            opt.title = `
                <a id="summary-title-popup" tabindex="0" href="#" class="btn btn-link" style="padding: 2px;" role="button" data-trigger="hover" data-toggle="popover" data-container="body" data-placement="bottom" data-content="${_.map(this.getSelectedEntries(), e => e.Nome).join(' - ')}">
                    ${_.size(this.getSelectedEntries())}
                </a>
                ${translate('Common.SelectedLocation')}
            `;
        }

        LayerInfo('Summary').create(opt);
    }

    _exportAutoParts (feature) {
        const opt = {
            type: this.getType(),
            context: this.options.context,
            visible: true,
            grouping: _.some(environment.get('CONFIG.FILTERS.MARKED_AREA_TYPES'), v => v.name === LayerControls(this.options.name).getTypeText())
        };
        if (feature) {
            opt.id = feature.properties.Id;
            opt.title = feature.properties['Nome'];
            opt.type = feature.properties['Tipo'];
        } else {
            opt.id = _.map(this.getSelectedEntries(), e => e.Id);
            opt.title = _.map(this.getSelectedEntries(), e => e['Nome']);
            opt.type = _.map(this.getSelectedEntries(), e => e['Tipo']);
        }

        if (opt.id.length > 1) {
            return notify(NOTIFY_TYPE.ERROR, translate("Validation.SelectOnlyOneArea"));
        }

        const optionsRequest = {
            IdLocale: opt.id[0],
            Title: opt.title,
            Type: opt.type,
        };

        api.themes.get.areas.exportAutoParts(optionsRequest);
    }

    _selectOneAreaMessage (name) {
        notify(NOTIFY_TYPE.LOG, translate('Validation.LocationRequired'));
        setTimeout(() => {
            LayerControls(this.options.name).getControls().checkbox[name].input.iCheck('uncheck');
        }, 100);
    }

    _filterByLocation (state) {
        if (state && this.getEntry().size === 0) {
            return this._selectOneAreaMessage('filterLocation');
        }

        if (!state && this.getEntry().size === 0) {
            this.getLayer().setOptions({ filter });
            const controls = LayerControls(this.options.name);
            if (controls.check()) {
                this.getLayer().reload();
            }
            return;
        }

        const values = [];
        this.getEntry().forEach((status, id) => values.push(id));

        const { filter = {} } = this.options.layer;
        if (!filter.properties) {
            filter.properties = {};
        }

        if (state && this.getEntry().size > 0) {
            filter.properties.Id = values;
        } else {
            if (filter.properties.Id) {
                delete filter.properties.Id;
            }
        }

        this.getLayer().setOptions({ filter });
        const controls = LayerControls(this.options.name);
        if (controls.check()) {
            this.getLayer().reload();
        }
    }

    _filterByAreas (state) {
        if (state && this.getEntry().size === 0) {
            return this._selectOneAreaMessage('filterLocation');
        }

        const layerList = ['MarkedArea', 'SelectedAreas', 'DirectSales', 'DNC'].filter(l => l !== this.options.name);
        const features = [];
        this.getEntryAsFeature().forEach((feature) => features.push(feature));

        if (state && this.getEntry().size) {
            this._applyLayerOverlap(layerList, 'shape', features);
        } else {
            this._applyLayerOverlap(layerList, 'shape', null);
        }
    }

    _filterByPoints (state) {
        if (state && this.getEntry().size === 0) {
            return this._selectOneAreaMessage('filterPoints');
        }

        const layerList = ['Dealerships', 'Competitors', 'StoreInactive', 'PointOfInterest', 'ClusterSales', 'ServiceArea', 'Customers', 'Cities', 'PGTS', 'Toll', 'FuelStation', 'WorkshopPart'];
        const features = [];
        this.getEntryAsFeature().forEach((feature) => features.push(feature));

        if (state && this.getEntry().size) {
            this._applyLayerFilter(layerList, 'shape', features);
        } else {
            this._applyLayerFilter(layerList, 'shape', null);
        }
    }

    _applyLayerFilter (layers, type, features) {
        _.forEach(layers, (name) => {
            try {
                const currentLayer = LayerControls(name).getLayer();
                if (currentLayer && currentLayer.options) {
                    const filter = currentLayer.options.filter || {};
                    filter[type] = features;
                    currentLayer.setOptions({ filter });
                    if (worldMap.hasLayer(currentLayer.layer)) {
                        currentLayer.reload();
                    }
                }
            } catch (err) {
                console.log(err);
            }
        });
    }

    _applyLayerOverlap (layers, type, features) {
        _.forEach(layers, (name) => {
            try {
                const controls = LayerControls(name);
                if (!controls) return;
                const currentLayer = controls.getLayer();
                if (currentLayer && currentLayer.options) {
                    const remote = currentLayer.options.remote || {};
                    remote.params.overlap = features ? features.map(f => f.properties.Id).join(',') : undefined;
                    currentLayer.setOptions({ remote });
                    if (worldMap.hasLayer(currentLayer.layer)) {
                        currentLayer.redraw();
                    }
                }
            } catch (err) {
                console.log(err);
            }
        });
    }

    create (features) {
        // console.time('Shapes -> create');

        this._mainContainer = this._getContainer();
        // do not render if there is not data
        const controls = LayerControls(this.options.name);
        if (infoBoard.dataUnavailable({
            features,
            container: this._mainContainer,
            isEnabled: controls.check()
        })) {
            return this.show(this._mainContainer);
        }

        this._status = !!features;

        const template = $(templates('infoboard-content', {
            id: `${this.options.name}-collapse`,
            actions: this._hasSummary()
        }));
        const content = template.find('.panel-group');

        let index = 0;

		features = [
				...features.filter(f => !this.check(f.properties.Id)),
				...this.getEntryAsFeature()
			].sort((a, b) => a.properties.Nome > b.properties.Nome ? 1 : -1);

        _(features)
            .forEach(f => {
                const item = this._content(this._mainContainer, f, index++);
                if (item) {
                    content.append(item.layout);
                }
            });

        // give the DOM a break
        $(this._mainContainer).detach().append(template).appendTo('#infoboard-content');

        if (this._hasSummary()) {
            infoBoard.enableSelection(this._mainContainer, this.options.name);
        }

        customScrollbar(content);
        // console.timeEnd('Shapes -> create');
    }

    // END: create: (features) ->
    show (container) {

        // console.time('Shapes -> show');

        // hide actions menu
        $(container).find('.panel-context').hide();

        if (this._status) {
            const disabled = this.getEntry().size === 0 ? 'disabled' : '';
            const $template = $(this._getTemplate('action-bar', {
                disabled,
                id: `${this.options.name}-submit`,
                reset: true
            }));
            $(container).append($template);

            $template.find('.action-submit').on('click', () => {
                Workspace.addAction(new Item(`#${$template.prop('id')} .action-submit`, '', ControlType.InputButton));
                this._handleClickAction();
            });

            $template.find('.action-cancel').on('click', () => {
                Workspace.addAction(new Item(`#${$template.prop('id')} a[data-target="select_none"]`, '', ControlType.InputButton));
                return $('a[data-target="select_none"]').trigger('click');
            });
        }
    }

    _handleClickAction = () => {
        const entries = this.getSelectedEntries().filter(e => $(`#${this.options.name}-selection-${e.Id}`).prop('checked'));

        if (_.size(entries) === 0) {
            return notify(NOTIFY_TYPE.ERROR, translate('Error.NoDataAvailable'));
        };

        const locale = this.getSelectedEntries()[0];

        const localesMarkedAllowed = Object.values(environment.get('CONFIG.FILTERS.MARKED_AREA_TYPES')).filter(x => x.neocomLocaleType >= LocaleTypeEnum.AreaOperational).map(x => x.id);
        const localeEntryHasAllowedToShow = localesMarkedAllowed.includes(locale.IdTipoLocalidade);

        const onlyOneSelected = this.getSelectedEntries().length === 1;

        const showWorkshopAndAutoparts = (this.options.context === 'operational-area') && isAuthorized(FeaturePermission.LAYER_WORKSHOP_PART.export) && localeEntryHasAllowedToShow;

        const options: routingOption[] = [
            {
                text: `Sumário ${translate('Common.Customers')}`,
                handler: () => {
                    this._resetCustomersSummaryFilters();
                    this._showCustomersPanel.bind(this)();
                    Workspace.addAction(new Item('form.action-popup button[data-value="0"]', '', ControlType.InputButton));
                },
                hidden: false
            },
            {
                text: translate('Common.Summary'),
                handler: (e) => {
                    this._showSummary.bind(this)();
                    Workspace.addAction(new Item('form.action-popup button[data-value="1"]', '', ControlType.InputButton));
                },
                hidden: false
            },
            {
                text: translate('Common.WorkshopPart'),
                handler: () => {
                    this._exportAutoParts.bind(this)();
                    Workspace.addAction(new Item('form.action-popup button[data-value="2"]', '', ControlType.InputButton));
                },
                hidden: !showWorkshopAndAutoparts
            },
        ];

        if(onlyOneSelected && locale.IdTipoLocalidade === localeType.city){
            const {Id, Nome} = locale;

            const originData: originDistanceInfo = {
                id: Id,
                name: Nome,
                type: 'fromCity'
            };

            new DistanceReportInfo(originData, options);
            return;
        }

        dialog.select(options.filter(x => !x.hidden).map((x, i) => ({ value: i, text: x.text })), chosenOption => {
            const {handler} = options[chosenOption];
            return handler();
        });
    };

    highlight (feature, options) {
        this.getLayer().highlight(feature, options);
    }

    unhighlight (feature, options) {
        this.getLayer().unhighlight(feature, options);
    }

    blank () {
        // console.time('Shapes -> blank');
        _(this.getLayer().getCurrentFeatures())
            .filter(f => f ? !this.check(f.properties.Id) : false)
            .forEach(f => this.getLayer().unhighlight(f));

        // console.timeEnd('Shapes -> blank');
    }

    reset () {
        // console.time('Shapes -> reset');

        this.getEntry().clear();
        this._selectedAreaId = undefined;

        const controls = LayerControls(this.options.name);

        if (controls.check()) {
            this.unhighlight(true);
            controls.reload();
        }

        // console.timeEnd('Shapes -> reset');
    }

    add (feature, layer) {
        // console.time('Shapes -> add');
        if (!feature) return;

        infoBoard.open();

        this._selectedAreaId = String(feature.properties.Id);
        this.setEntry(this._selectedAreaId, true);

        $(`#${this.options.name}-tab-link`).click();
        if (this.getEntry().size > 0) {
            $(`#${this.options.name}-submit`).find('.action-submit').attr('disabled', false);
        }

        this.highlight(feature, {
            fixed: true,
            layer
        });

        if (this._filterAreas) {
            this._filterByAreas(true);
        }

        if (this._filterLocation) {
            this._filterByLocation(true);
        }

        if (this._filterPoints) {
            this._filterByPoints(true);
        }

        // console.timeEnd('Shapes -> add');
    }

    remove (feature, layer) {
        // console.time('Shapes -> remove');

        const featureId = String(feature.properties.Id);

        if (this.getEntry().size < 1) {
            $(`#${this.options.name}-submit`).find('.action-submit').attr('disabled', true);
        }

        this.unhighlight(feature, {
            fixed: false,
            layer
        });

        this.getEntry().delete(featureId);

        const controls = LayerControls(this.options.name);
        if (controls && controls.removeManualPaint) {
            controls.removeManualPaint(featureId);
        }

        if (this._filterAreas) {
            this._filterByAreas(true);
        }

        if (this._filterLocation) {
            this._filterByLocation(true);
        }

        if (this._filterPoints) {
            this._filterByPoints(true);
        }

        // console.timeEnd('Shapes -> remove');
    }

    getLastSelectedAreaId () {
        return this._selectedAreaId;
    }

    getStatus () {
        return this._status;
    }

    set (value) {
        this._status = value;
    }

    check (id) {
        return this.getEntry().has(String(id));
    }

    setEntry (id, value) {
        const entry = this.getEntry();
        entry.set(String(id), value);
    }

    getEntry () {
        return this.getLayer().state.highlight;
    }

    getEntryAsFeature () {
        const entries = [];
        const cacheEntrie = environment.get('DNC.ENTRIES');
        if(!_.isUndefined(cacheEntrie)) {
            _.map(cacheEntrie, f => {
                this.setEntry(f.properties.Id, true);
                this.highlight(f, {
                    fixed: true,
                    undefined
                });
            });

        }
        this.getLayer().state.highlight.forEach((status, id) => entries.push(id));

        const features = [];
        this.getLayer().state.features.forEach((feature, id) => {
            if (_.some(entries, (e) => String(e) === String(id))) {
                features.push(feature);
            }
        });

        return features;
    }

    getSelectedEntries () {
        const out = [];
        this.getEntryAsFeature().forEach((feature) => {
            out.push(feature.properties);
        });

        const layerTypeId = LayerControls(this.options.name).getTypeValue();
        return _.filter(out, p => p.IdTipoLocalidade === layerTypeId);
    }

    values (visible = false) {
        // console.time('Shapes -> values');

        const output = {
            id: [],
            type: Number(LayerControls(this.options.name).get().scheme())
        };

        if (visible) {
            this.getLayer().getCurrentFeatures().forEach(feature => output.id.push(feature.properties.Id));
        } else {
            this.getEntry().forEach((status, id) => output.id.push(id));
        }

        // console.timeEnd('Shapes -> values');
        return output;
    }

    setFilterByPoints (state: boolean) {
        this._filterPoints = state;
        this._filterByPoints(state);
    }

    setFilterByLocation (state: boolean) {
        this._filterLocation = state;
        this._filterByLocation(state);
    }

    setFilterByAreas (state: boolean) {
        this._filterAreas = state;
        this._filterByAreas(state);
    }
}

export default ShapesInfo;



// WEBPACK FOOTER //
// ./src/scripts/info/ShapesInfo.js